import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Pricing from "@/pages/Pricing";
import PaymentReturn from "@/pages/PaymentReturn";
import About from "@/pages/About";
import Trading from "@/pages/Trading";
import Contact from "@/pages/Contact";
import Network from "@/pages/Network";
import AdminNetwork from "@/pages/AdminNetwork";
import AdminNFP from "@/pages/AdminNFP";
import AdminLearning from "@/pages/AdminLearning";
import Learning from "@/pages/Learning";
import News from "@/pages/News";
import Profile from "@/pages/Profile";
import Token from "@/pages/Token";
import Lottery from "@/pages/Lottery";
import Championship from "@/pages/Championship";
import api from "@/lib/api";
import { logWarn } from "@/lib/logger";

function Loader() {
  return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento...</div>;
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
        setUser(data.user);
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <Loader />;
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return (
    <>
      <Navbar />
      {children}
    </>
  );
}

function LandingGate() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/about" element={<About />} />
      <Route path="/trading" element={<Trading />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/lottery" element={<Lottery />} />
      <Route path="/championship" element={<Championship />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/markets" element={<Navigate to="/dashboard" replace />} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/learning" element={<Protected adminOnly><Learning /></Protected>} />
      <Route path="/news" element={<Protected><News /></Protected>} />
      <Route path="/wallet" element={<Protected adminOnly><Token /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="/admin/network" element={<Protected adminOnly><AdminNetwork /></Protected>} />
      <Route path="/admin/nfp" element={<Protected adminOnly><AdminNFP /></Protected>} />
      <Route path="/admin/learning" element={<Protected adminOnly><AdminLearning /></Protected>} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/payment-return" element={<Protected><PaymentReturn /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((e) => logWarn("SW registration failed", e));
    }
  }, []);

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
